import React from 'react';
import { RotateCcw } from 'lucide-react';
import ConfirmModal from '../ui/ConfirmModal';
import { api } from '../../api/client';
import type { PayPeriodClosedRefusal } from '../../types/pay-period-reopen';

/**
 * The closed-period refusal and its one way out, shared by every write that can
 * meet it: the pay period screen's save, create-adjustment and add-employee
 * (#665, #666), and the approval queue's office correction.
 *
 * Moved here out of PayrollScreen.tsx rather than copied, when the office
 * correction became the fourth caller. Three copies of "what does a 409 with
 * canReopen mean, and what does the confirmation promise" had already been
 * avoided by keeping them in one screen; a second screen was the point at which
 * that stopped working.
 */

export interface PeriodClosed {
  message: string;
  canReopen: boolean;
  /**
   * The period that REFUSED — `details.periodId` (api#720) — or null when the
   * 409 does not name one. Not always the record's own period: an office
   * correction to a shop shift is also gated on the later period its pay was
   * carried into. Resolve it through `reopenTargetFor`, never read it bare.
   */
  periodId: string | null;
}

/**
 * A refusal because the period is no longer open, read off a write's 409.
 *
 * save, adjustments, add-employee and the office timesheet correction all
 * answer through the API's period gate with `details: { status, canReopen }`,
 * and for a long time nothing here read `canReopen`: the office was told
 * "Reopen the period first" with no way in sight. `canReopen` is the API's own
 * mirror of reopen's three preconditions, so it is trusted as-is rather than
 * re-derived from a status on screen — a stale tab is exactly when that status
 * is wrong.
 *
 * Returns null for anything else, including a 409 with no boolean `canReopen`,
 * so an unrelated conflict never grows a Reopen pointer.
 */
export function readPeriodClosed(err: any): PeriodClosed | null {
  const res = err?.response;
  if (res?.status !== 409) return null;
  const details = (res.data as Partial<PayPeriodClosedRefusal> | undefined)?.details;
  const canReopen = details?.canReopen;
  if (typeof canReopen !== 'boolean') return null;
  const periodId = typeof details?.periodId === 'string' && details.periodId ? details.periodId : null;
  return { message: res.data?.error || 'This pay period is no longer open', canReopen, periodId };
}

/**
 * The period a Reopen offered from this refusal has to target: the one that
 * refused, falling back to the record's own period when the 409 names none.
 *
 * The record's own period is the wrong answer whenever the two differ, and it
 * fails quietly: reopen is idempotent on an open period, so a Reopen aimed at
 * the shift's still-open period answers "already open", the save is refused
 * again, and the office has no way through. The fallback exists only because
 * not every 409 shape is guaranteed to carry `periodId` yet.
 */
export function reopenTargetFor(closed: PeriodClosed, ownPeriodId: string | null | undefined): string | null {
  return closed.periodId ?? ownPeriodId ?? null;
}

/**
 * POST /pay-periods/:id/reopen, reduced to what a caller shows. Never throws.
 *
 * On success the message is the API's — it carries the timesheet count, or
 * says the period was already open. On failure it is the API's too: a 409 here
 * is permanent (exported, or a payroll export exists) and its message says
 * what to do instead.
 */
export async function reopenPayPeriod(periodId: string): Promise<{ ok: boolean; message: string }> {
  try {
    const res = await api.payPeriods.reopen(periodId);
    return { ok: true, message: res.data?.message || 'Pay period reopened' };
  } catch (err: any) {
    return { ok: false, message: err?.response?.data?.error || 'Failed to reopen the pay period' };
  }
}

/** The button a closed-period refusal carries. It only ever opens the confirmation. */
export function ReopenPeriodButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className="flex items-center gap-1.5 px-3 py-1.5 bg-fieldiq-card border border-fieldiq-border rounded-lg text-xs text-white font-medium hover:bg-fieldiq-dark disabled:opacity-50">
      <RotateCcw size={14} /> Reopen period
    </button>
  );
}

/**
 * Every clause is reopenPeriod (pay-periods.service.ts), in one transaction:
 * status back to 'open', approved_at and approved_by cleared, each employee's
 * timesheet back to 'draft' (approve had set every one of them to 'approved'),
 * and a tenant_audit_log row.
 *
 * It says NOTHING about paid or exported periods. Only an approved period ever
 * reaches this dialog, and a sentence about the other states would be a second
 * copy of the mark-paid dialog's claims with nothing keeping the two in step.
 *
 * `periodLabel` names the period as the caller knows it — its date range on the
 * pay period screen; the shift it holds on the approval queue, which never
 * loads the period.
 */
export function ReopenPayPeriodConfirm({ isOpen, periodLabel, loading, onConfirm, onCancel }: {
  isOpen: boolean;
  periodLabel: React.ReactNode;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <ConfirmModal
      isOpen={isOpen}
      title="Reopen this pay period?"
      confirmLabel="Yes, reopen"
      confirmVariant="primary"
      loading={loading}
      onConfirm={onConfirm}
      onCancel={onCancel}
      body={
        <>
          <p className="text-white font-medium">
            {periodLabel} returns to Open.
          </p>
          <p>
            The approval is cleared, and every employee's timesheet in this period goes
            back to draft. The reopen is recorded in the audit log.
          </p>
          <p className="text-fieldiq-muted">
            Approve the period again once your changes are made.
          </p>
        </>
      }
    />
  );
}
